import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToCpp = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const generateCppStructs = (name, obj, structs = []) => {
    let lines = [`struct ${name} {`];

    for (const key in obj) {
      const value = obj[key];
      let fieldName = key;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedName = `${name}_${capitalize(key)}`;
        generateCppStructs(nestedName, value, structs);
        lines.push(`  ${nestedName} ${fieldName};`);
      } else {
        const type = inferCppType(value);
        lines.push(`  ${type} ${fieldName};`);
      }
    }

    lines.push(`};`);
    structs.unshift(lines.join('\n'));
    return structs;
  };

  const inferCppType = (value) => {
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'double';
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'string') return 'std::string';
    return 'void*';
  };

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      if (typeof json !== 'object') throw new Error('Root must be an object');
      const structs = generateCppStructs('MyStruct', json);
      setOutputCode(structs.join('\n\n'));
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n" + e.message);
    }
  };

    return (
      <div className="formatter-container">
        <h1>JSON ➝ C++ Converter </h1>

        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={handleConvert} className="btn primary">
            <FaBrush /> Convert
          </button>
          <button onClick={() => handleDownload("c", "formatted-c")} className="btn success">
            <FaDownload /> Download
          </button>
  		<button onClick={handleClear} className="btn danger">
            <FaTrash /> Clear
          </button>

        </div>

        {/* Editors */}
        <div className="editor-panels">
          <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
          <div className="editor-section-empty-div">
          </div>
          <JsonEditorPanel title="Output C++" language="c++"  code={outputCode} />
        </div>
		
		<CommonSeoContent
  title="Online JSON to C++ Class Converter"
  description="Convert JSON to C++ class definitions with jsonconverter.org. Supports nested objects, vectors, and generates clean C++ code with proper data types."
  keywords="JSON to C++, json to cpp, json to c++ class generator, json to c++ converter, json to struct c++, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use JSON to C++ Converter?"
  whyUseText="Use our JSON to C++ Converter to turn structured JSON into fully-typed C++ class or struct definitions. This tool is ideal for developers working in high-performance environments, game engines, or data modeling applications. It supports nested structures, arrays via `std::vector`, and maps JSON types to modern C++ types for compatibility with libraries like RapidJSON or nlohmann/json. Everything runs locally in your browser — no need to install anything or upload sensitive data. It’s fast, private, and completely free."
  howToUseSteps={[
    "Paste your JSON into the input editor.",
    "Click Convert to generate C++ classes.",
    "Review or copy the generated C++ code.",
    "Use the output directly in your application.",
  ]}
  features={[
    "Converts JSON to C++ classes or structs",
    "Supports std::vector for arrays and nested objects",
    "Generates modern, type-safe C++ code",
    "Works offline in your browser — no server needed",
    "Free tool — no login required",
    "Option to download C++ output for later use",
  ]}
  inputTitle="Input JSON:"
  inputExample={`{
  "name": "Alice",
  "age": 22,
  "skills": ["C++", "Python"]
}`}
  outputTitle="Output C++:"
  outputExample={`class User {
public:
  std::string name;
  int age;
  std::vector<std::string> skills;
};`}
  callToAction="Start converting your JSON to C++ classes instantly — no signup required."
/>
      </div>
    );
};

export default JsonToCpp;
